/**
 * Derives the notice banner's palette from the single colour staff pick in the admin form.
 *
 * The colour is used as-is: a low-alpha wash behind the banner, a stronger version of the same for the
 * border, and the full colour for the icon, title and left stripe. Nothing is darkened or shifted, so
 * what staff pick in the colour input is what they get. The body text is left to the stylesheet's fixed
 * dark neutral, which is what keeps the message readable whichever colour is chosen.
 */

export const DEFAULT_BANNER_COLOUR = '#ffe074';

interface Rgb {
	r: number;
	g: number;
	b: number;
}

/**
 * Parses `#rgb` / `#rrggbb` (with or without the leading hash). Returns null for anything else —
 * callers fall back to the default banner colour rather than rendering something unreadable.
 */
export function parseHexColour(value: string | null | undefined): Rgb | null {
	if (!value) {
		return null;
	}

	const hex = value.trim().replace(/^#/, '');

	if (/^[0-9a-f]{3}$/i.test(hex)) {
		return {
			r: parseInt(hex[0] + hex[0], 16),
			g: parseInt(hex[1] + hex[1], 16),
			b: parseInt(hex[2] + hex[2], 16),
		};
	}

	if (/^[0-9a-f]{6}$/i.test(hex)) {
		return {
			r: parseInt(hex.slice(0, 2), 16),
			g: parseInt(hex.slice(2, 4), 16),
			b: parseInt(hex.slice(4, 6), 16),
		};
	}

	return null;
}

const toRgba = ({ r, g, b }: Rgb, alpha: number) => `rgba(${r}, ${g}, ${b}, ${alpha})`;

const resolve = (colour: string | null | undefined): Rgb =>
	parseHexColour(colour) ?? parseHexColour(DEFAULT_BANNER_COLOUR)!;

/** The banner's icon, title and left stripe colour: the chosen colour, unaltered. */
export function getBannerAccentColour(colour: string | null | undefined): string {
	return toRgba(resolve(colour), 1);
}

/** The soft wash behind the banner. */
export function getBannerTintColour(colour: string | null | undefined): string {
	return toRgba(resolve(colour), 0.12);
}

/** The banner's outline, a stronger version of the tint so the block still has an edge. */
export function getBannerBorderColour(colour: string | null | undefined): string {
	return toRgba(resolve(colour), 0.45);
}
